import { requestEmailChangeInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { trpc } from '@/shared/api';
import { getErrorMessage, mapServerValidationErrors } from '@/shared/lib';

const FIELD_KEYS = ['email', 'password'] as const;

interface RequestEmailChangeInput {
  readonly email: string;
  readonly password?: string;
}

interface UseRequestEmailChangeResult {
  readonly emailError: string | null;
  readonly passwordError: string | null;
  readonly formError: string | null;
  readonly isPending: boolean;
  /** 확인 메일을 보낸 (소문자로 정규화된) 새 주소. 보내기 전에는 null. */
  readonly sentTo: string | null;
  readonly submit: (input: RequestEmailChangeInput) => Promise<void>;
}

/** 이메일 변경 요청(auth.requestEmailChange) — 비밀번호 계정은 현재 비밀번호를 확인하고, 새 주소로 확인 메일을 보낸다. */
export const useRequestEmailChange = (): UseRequestEmailChangeResult => {
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const mutation = trpc.auth.requestEmailChange.useMutation();

  const submit = async ({ email, password }: RequestEmailChangeInput): Promise<void> => {
    setFormError(null);
    setPasswordError(null);
    setSentTo(null);

    const parsed = requestEmailChangeInputSchema.safeParse({
      email: email.trim().toLowerCase(),
      ...(password ? { password } : {}),
    });

    if (!parsed.success) {
      setEmailError(parsed.error.issues[0]?.message ?? '이메일을 확인해 주세요.');
      return;
    }

    setEmailError(null);

    try {
      await mutation.mutateAsync(parsed.data);
      setSentTo(parsed.data.email);
    } catch (error) {
      const serverErrors = mapServerValidationErrors(error, FIELD_KEYS);

      if (serverErrors) {
        setEmailError(serverErrors.fieldErrors.email ?? null);
        setPasswordError(serverErrors.fieldErrors.password ?? null);
        setFormError(serverErrors.formError);
      } else {
        setFormError(getErrorMessage(error, '보내지 못했습니다. 잠시 후 다시 시도해 주세요.'));
      }
    }
  };

  return { emailError, passwordError, formError, isPending: mutation.isPending, sentTo, submit };
};
